import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../utils/Auth';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../API';
import styles from './Upgrade.module.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import { handleStripePriceId } from '../../lib/handleStripePriceId';
import { useGetStripeName } from '../../utils/hooks/useGetStripeName';

const { createStripeCustomerAPI, createStripeCheckout, createStripeCustomerSession, updateUserObject } = API;

const createCheckoutSession = async ({ stripeId, subId, setLoading, planId }) => {
    // check if there is a current subscription, if not create new, if there is move to 
    // process to edit subscription
    try {
        if (subId) {
            const response = await createStripeCustomerSession({ cust_id: stripeId });
            if (response.data.url) {
                setLoading(false);
                window.location.assign(response.data.url);
            };
        } else {
            const response = await createStripeCheckout({ customer_id: stripeId, price_id: planId });
            if (response.data.result === 'redirect') {
                setLoading(false);
                window.location.assign(response.data.url);
            };
        };
    } catch (err) {
        console.error("Error creating Stripe checkout session: ", err);
    };
};

const createStripeCustomer = async (data) => {
    const { uid } = data;

    try {
        const res = await createStripeCustomerAPI(data);
        const customer = res?.data?.customer;
        if (customer?.id) {
            await updateUserObject(uid, { stripe_id: customer?.id });
            return customer.id;
        };
    } catch (err) {
        console.error("Error creating Stripe customer: ", err);
        return err;
    };
};

export default function Upgrade() {
    const { userObj, orgObj, activeOrgId, currentUser, isLoading } = useAuth();
    const navigate = useNavigate();
    const query = window.location.search;
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const canceled = useMemo(() => searchParams.get('canceled') === 'true',[searchParams]);
    const success = useMemo(() => searchParams.get('success') === 'true' || false, [searchParams]);
    const tier = useMemo(() => searchParams.get('tier'), [searchParams]);
    const isAnnual = useMemo(() => searchParams.get('annual') === 'true',[searchParams]);
    const planId = useMemo(() => handleStripePriceId({ tier, isAnnual }), [tier,isAnnual]);
    const currentPlan = useGetStripeName(orgObj?.plan?.stripe_plan);
    const planToChange = useGetStripeName(planId)

    const capitalizeFirstLetter = (str) => {
        if (typeof str !== 'string' || str.length === 0) return str;

        const firstChar = str[0];
        if (!/^[a-zA-Z]$/.test(firstChar)) return str;

        return firstChar.toUpperCase() + str.slice(1);
    };

    const checkoutClick = useCallback(async () => {
        if (!loading) {
            const customer = (currentUser?.uid === userObj?.org_id) && (userObj?.stripe_id ? userObj?.stripe_id : await createStripeCustomer({ email: currentUser?.email, uid: currentUser?.uid }));
            setLoading(true);
            createCheckoutSession({ stripeId: customer, subId: userObj?.sub_id, setLoading, planId: (planId === 0 || planId) });
        };
    }, [currentUser, userObj, loading]);

    useEffect(() => {
        let timeoutId;
        if (planId && currentUser && userObj && !orgObj?.plan?.stripe_plan) {
            timeoutId = setTimeout(() => {
                checkoutClick();
            }, 100);
        };

        return () => timeoutId && clearTimeout(timeoutId);
    }, [planId, currentUser, userObj, orgObj,]);

    useEffect(() => {
        // Don't redirect while auth is still loading
        if (isLoading) return;

        // Invalid/failed upgrade attempts go to pricing
        if (!success && (canceled || planId === 'unknown')) {
            window.location.assign('https://liquidqr.com/pricing/');
            return;
        }

        // Fresh signups landing here without a tier should go home
        if (!success && planId === undefined) {
            navigate('/home', { replace: true });
        }
    },[canceled, planId, success, isLoading, navigate]);

    if (!isLoading && !currentUser) return <Navigate to={`/login${query ? (query?.includes('upgrade') ? query : `${query}&r=upgrade`) : '?r=upgrade'}`} />

    if (!isLoading && tier && orgObj && orgObj?.plan?.stripe_plan !== planId) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <div className={styles.card}>
                        <div className={styles.textContainer}>
                            <div className={styles.text}>Change plan to {capitalizeFirstLetter(planToChange)}?</div>
                            <div className={styles.subText}>Current plan {currentPlan}</div>
                            <div className={styles.subText}>Existing balance will roll over or be deducted from previous payment method</div>
                        </div>
                        <div className={styles.btnGroup}>
                            <button className={styles.accept} onClick={checkoutClick} disabled={loading} >{loading ? <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center' }}><LoadingSpinner /></div> : 'Continue'}</button>
                            <button className={styles.cancel} onClick={() => navigate('/organizations/settings')} >Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (planId === 0 || success || orgObj?.plan?.stripe_plan === planId) return <Navigate to='/home' />

    return (
        <div className={styles.wrapper} >
            <div className={styles.container} >
                <LoadingSpinner />
            </div>
        </div>
    );
};
